import React, { useState, useEffect, useMemo } from "react";
import { Layout, Menu } from "antd";
import { sidebarItems } from "./SidebarData";
import { Link, useLocation } from "react-router-dom";
import { primaryColor } from "../../../constants/colors";
import { useRole } from "../../../hooks/useRole";

const { Sider } = Layout;
const { SubMenu } = Menu;

const Sidebar = () => {
  const location = useLocation();
  const [openKeys, setOpenKeys] = useState([]);
  const { user } = useRole();

  // --- Hooks first, before any early return ---
  const filteredSidebarItems = useMemo(() => {
    if (!user) return [];

    if (user.role === "super_admin") return sidebarItems;

    return sidebarItems
      .map((item) => {
        let children = [];
        if (item.children && item.children.length > 0) {
          children = item.children.filter((child) =>
            user.permissions.includes(child.module)
          );
        }

        if (user.permissions.includes(item.module) || children.length > 0) {
          return { ...item, children };
        }

        return null;
      })
      .filter(Boolean);
  }, [user]);

  const findSelectedKey = (items, path) => {
    for (const item of items) {
      if (item.url === path) return item.id;
      if (item.children) {
        const childKey = findSelectedKey(item.children, path);
        if (childKey) return childKey;
      }
    }
    return null;
  };
  const selectedKey = findSelectedKey(filteredSidebarItems, location.pathname);

  useEffect(() => {
    const findParentKey = (items, path) => {
      for (const item of items) {
        if (item.url === path) return item.id;
        if (item.children) {
          const parentKey = findParentKey(item.children, path);
          if (parentKey) return item.id;
        }
      }
      return null;
    };
    const parentKey = findParentKey(filteredSidebarItems, location.pathname);
    setOpenKeys(parentKey ? [parentKey] : []);
  }, [location.pathname, filteredSidebarItems]);

  const onOpenChange = (keys) => setOpenKeys(keys);

  // --- Early return after hooks ---
  if (!user) return <div>Loading...</div>;

  return (
    <Sider
      width={200}
      className="site-layout-background d-none d-lg-block"
      style={{
        borderRight: "2px solid #e9edf4",
        position: "fixed",
        height: "100vh",
        overflowY: "auto",
      }}
    >
      <Menu
        mode="inline"
        selectedKeys={[selectedKey]}
        openKeys={openKeys}
        onOpenChange={onOpenChange}
        style={{ height: "100%", borderRight: 0 }}
        theme="light"
      >
        {filteredSidebarItems.map((menu) =>
          menu.children && menu.children.length > 0 ? (
            <SubMenu
              key={menu.id}
              icon={menu.icon}
              title={<span>{menu.label}</span>}
              style={{
                color:
                  selectedKey === menu.id ||
                  menu.children.some((child) => selectedKey === child.id)
                    ? primaryColor
                    : "#6d758a",
                backgroundColor:
                  selectedKey === menu.id ||
                  menu.children.some((child) => selectedKey === child.id)
                    ? "#ADD8E6"
                    : "#ffffff",
              }}
            >
              {menu.children.map((item) => (
                <Menu.Item
                  key={item.id}
                  icon={item.icon}
                  style={{
                    color: selectedKey === item.id ? primaryColor : "#6d758a",
                    backgroundColor:
                      selectedKey === item.id ? "#ADD8E6" : "#ffffff",
                  }}
                >
                  <Link className="link-unstyled" to={item.url}>
                    {item.label}
                  </Link>
                </Menu.Item>
              ))}
            </SubMenu>
          ) : (
            <Menu.Item
              key={menu.id}
              icon={menu.icon}
              style={{
                color: selectedKey === menu.id ? primaryColor : "#6d758a",
                backgroundColor:
                  selectedKey === menu.id ? "#ADD8E6" : "#ffffff",
              }}
            >
              <Link className="link-unstyled" to={menu.url}>
                {menu.label}
              </Link>
            </Menu.Item>
          )
        )}
      </Menu>
    </Sider>
  );
};

export default Sidebar;
